import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { LocalStorageService } from '@app/core/services/localStorage/local-storage.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { FilterModel } from '@app/shared/models/product';
import { ProductList } from '@app/shared/models/product';
import { APIResponse } from '@app/shared/services/core/ICore.interface';
import { EncrDecrService } from '@app/shared/services/encr-decr-service.service';
import { ErrorhandlerService } from '@app/shared/services/errorhandler/errorhandler.service';
import { IMasterProductDetails, IMasterProductImage, IMasterProductSizeDetails, IMasterProductStyleDetails } from '@app/shared/services/product/getProductDetailsByProductId.interface';
import { ProductService } from '@app/shared/services/product/product.service';
import { StateService } from '@app/shared/services/state/state.service';
import { environment } from '@env/environment';
import { InksofteditorInterfaceData } from '../../inksofteditor/inksofteditor.interface';
import { Location } from '@angular/common';
import { Constants } from '../../../constants';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-master-product-detail',
  templateUrl: './master-product-detail.component.html',
  styleUrls: ['./master-product-detail.component.css']
})
export class MasterProductDetailComponent implements OnInit {
  imageSrc: string;
  imageList: string[];
  imageListAll: IMasterProductImage[];
  productId!: any;
  productDetails: IMasterProductDetails;
  sizeList: IMasterProductSizeDetails[]; // For sizes of particular style  

  currentStyleUniqueId: number;
  currentStyleName: string;
  currentSizeName: string;
  currentPrice: number;

  // Local storage fields
  userRole: any;
  masterProductImgBaseURL = environment.masterProductImgBaseURL;

  displayColumns: string[];
  dataSource: any;
  recommendationList: ProductList[];
  CustomizationType: string;
  IsEmbroideryType: boolean = false;
  EmbroideryCost: number;
  EmbroiderySetUpCost: number;

  // ------------------------------------
  constructor(private router: Router, private route: ActivatedRoute, private productService: ProductService,
    private notificationService: NotificationService, private loader: LoaderService,
    private errorhandlerService: ErrorhandlerService, private localstorageService: LocalStorageService,
    private EncrDecr: EncrDecrService,
    private stateService: StateService,
    private location: Location, private sanitizer: DomSanitizer) {
  }

  ngOnInit() {
    this.userRole = this.localstorageService.get('role');
    this.route.params.subscribe(params => {
      this.productId = params['id']; //Number(this.EncrDecr.get(CryptoKeyValue.Key, params.pid));

      this.productDetails = {
        ID: 0,
        DefaultStyleId: 0,
        Keywords: [],
        LongDescription: '',
        Name: '',
        Sides: [],
        Styles: [],
        ProductCareInstrictions: [],
        ProductFeatures: [],
        SizeGuides: [],
        ProductAdditionalInfo: null
      };
      this.imageList = [];
      this.imageListAll = [];
      this.sizeList = [];
      this.currentStyleUniqueId = 0;
      this.currentStyleName = '';
      this.currentSizeName = '';
      this.currentPrice = 0;
      this.recommendationList = [];

      this.getProductDetails();
      window.scroll(0, 0);
    });
  }

  /** Function to display product color(s) in color selector */
  displayColor(obj: any) {
    return {
      'background': obj
    };
  }

  /** On clicking the product image thumbnail in carousel */
  selectProductStyle(style: string) {
    this.imageSrc = style;
  }

  /** On selecting the product color from color selector */
  selectStyle(uniqueStyleId: number) {
    this.currentStyleUniqueId = uniqueStyleId;
    let style = this.productDetails.Styles.find(s => s.ID == uniqueStyleId);
    this.sizeList = style.Sizes;
    this.currentStyleName = style.Name;
    // set previously selected Size
    if (this.currentSizeName) {
      this.fn_SelectSize(this.currentSizeName);
    } else {
      this.fn_SelectSize(this.sizeList[0].Name);
    }

    // Assigning selected style images to display
    this.imageList = [];
    this.imageList = this.imageListAll.find(i => i.UniqueStyleId == uniqueStyleId).Images;
    this.imageSrc = this.imageList[0];
    this.fn_GetSizeQty(this.productId,uniqueStyleId);
  }

  /** Gets price based on product size */
  fn_SelectSize(sizeName: string) {
    const selectedSize = this.productDetails.Styles.find(x => x.ID == this.currentStyleUniqueId).Sizes.find(x => x.Name == sizeName);
    if (selectedSize && !this.IsEmbroideryType) {
      this.currentSizeName = selectedSize.Name;
      this.currentPrice = selectedSize.UnitPrice;
    } else {
      this.currentSizeName = selectedSize.Name;
      this.currentPrice = selectedSize.UnitPrice + (this.EmbroideryCost);
    }
  }

  mapImage(res: IMasterProductStyleDetails[]): IMasterProductImage[] {
    const imagesList = res.reduce((arr: IMasterProductImage[], x: IMasterProductStyleDetails) => {
      let imagePath = [];
      if (x.ImageFilePath_Front) {
        imagePath.push(this.getImageUrl(x.ImageFilePath_Front));
      }
      if (x.ImageFilePath_Back) {
        imagePath.push(this.getImageUrl(x.ImageFilePath_Back));
      }
      if (x.ImageFilePath_SleeveLeft) {
        imagePath.push(this.getImageUrl(x.ImageFilePath_SleeveLeft));
      }
      if (x.ImageFilePath_SleeveRight) {
        imagePath.push(this.getImageUrl(x.ImageFilePath_SleeveRight));
      }
      arr.push({
        Images: imagePath,
        UniqueStyleId: x.ID
      });
      return arr;
    }, []);
    return imagesList;
  }

  /** Helper function to get correct image URL (Azure or InkSoft) */
  getImageUrl(imagePath: string): string {
    if (!imagePath) {
      return '';
    }
    // Check if the image URL is already a full URL (Azure Blob Storage)
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Legacy Inksoft images - prepend Inksoft base URL
    return this.masterProductImgBaseURL + imagePath;
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /** Gets master product details */
  getProductDetails() {
    this.loader.show();
    this.productService
      .getProductDetailsByProductId(this.productId)
      .subscribe((res: APIResponse<IMasterProductDetails>) => {
        this.loader.hide();
        if (res.StatusCode === 200) {
          this.productDetails = res.Data.ResponseData;
          this.productDetails.LongDescription = this.sanitizeHtml(res.Data.ResponseData?.LongDescription) as string;
          this.IsEmbroideryType = this.productDetails.ProductAdditionalInfo ? this.productDetails.ProductAdditionalInfo.Enable_Embroidery : false;
          this.CustomizationType = this.productDetails.ProductAdditionalInfo && this.productDetails.ProductAdditionalInfo.Enable_Embroidery ? Constants.EmbroideryType : Constants.ScreenPrint;
          this.EmbroideryCost = this.productDetails.EmbroideryCost,
            //this.EmbroiderySetUpCost = this.productDetails.EmbroiderySetUpCost,
            this.imageListAll = this.mapImage(res.Data.ResponseData.Styles);
              this.selectStyle(this.productDetails.Styles[0].ID);
          this.displayColumns = this.getColumnHeader();
          this.dataSource = this.getDatasource();

          const recommededProductIds = res.Data.ResponseData.ProductRecommendationIds;
          if (recommededProductIds && recommededProductIds.length > 0) {
            this.recommendationList = [];
            this.getRecommendations(recommededProductIds);
          }
        } else {
          this.notificationService.error(res.Message, 'error');
        }
      }, (err: any) => {
        this.loader.hide();
        this.errorhandlerService.handleError(err);
      });
  }

  /** Gets recommendations */
  getRecommendations(productIds: number[]) {
    this.loader.show();
    const filter: FilterModel = {
      Skip: 0,
      Take: 20,
      Size: [],
      Brand: [],
      Color: [],
      InkSoftUserId: 0,
      searchBy: '',
      Category: '',
      ProductIds: productIds
    };
    this.productService.getAllProducts(filter).subscribe((res: any) => {
      this.loader.hide();
      if (res.StatusCode === 200) {
        if (res.Data.ProductList.length > 0) {
          for (let i = 0; i < res.Data.ProductList.length; i++) {
            const getStyleAttributes = this.fn_StyleAttribute(res.Data.ProductList[i].Styles, res.Data.ProductList[i].DefaultStyleId);
            const lowestUnitPrice = this.fn_FetchLowestUnitPrice(res.Data.ProductList[i].Styles);
            this.recommendationList.push({
              ProductId: res.Data.ProductList[i].ID,
              CategoryId: res.Data.ProductList[i].CategoryUniqueId,
              StyleId: res.Data.ProductList[i].DefaultStyleId,
              Description: res.Data.ProductList[i].Name,
              DesignId: '',
              Styles: res.Data.ProductList[i].Styles,
              ImageUrl: getStyleAttributes.ImageUrl,
              UnitCost: lowestUnitPrice,
              Sizes: getStyleAttributes.Sizes,
              IsActive: res.Data.ProductList[i].IsActive,
              ProductAdditionalInfo: res.Data.ProductList[i].ProductAdditionalInfo
            });
          }
        } else if (res.Data.ProductList.length === 0) {
          this.recommendationList = [];
        }
      }
    }, (err: any) => {
      this.loader.hide();
      this.errorhandlerService.handleError(err);
    });
  }

  /** Gets corresponding image to product...using the first style */
  fn_StyleAttribute(styleList: any, defaultStyleId: any) {
    // tslint:disable-next-line:triple-equals
    const styleData = styleList.find(s => s.ID == defaultStyleId);
    const orderBy = styleData.Sizes.sort((a, b) => a.SortOrder - b.SortOrder);
    return {
      Sizes: Array.prototype.map.call(orderBy, function (item) {
        return item.Name;
      }).join('  '),
      ImageUrl: this.masterProductImgBaseURL + styleData.ImageFilePath_Front,
      UnitCost: orderBy[0].UnitPrice
    };
  }
  // Get the lowest price from all sizes
  fn_FetchLowestUnitPrice(styleList: any) {
    const unitPriceList = styleList.reduce((current, next) => {
      next.Sizes.forEach(size => {
        current.push({
          UnitPrice: size.UnitPrice
        })
      });
      return current;
    }, []);
    const lowestUnitPrice = unitPriceList.sort((a, b) => a.UnitPrice - b.UnitPrice);
    return lowestUnitPrice[0].UnitPrice;
  }

  /** Opens inksoft editor for customizing a product */
  fn_CustomizeProduct() {
    let selectProduct: InksofteditorInterfaceData = {
      categoryId: this.productDetails.Categories,
      productId: this.productDetails.ID.toString(),
      styleId: this.currentStyleUniqueId.toString(),
      url: encodeURIComponent('store/iframe-popup?designID=0'),
      designId: '',
      sizes: this.sizeList,
      additionalInfo: this.productDetails.ProductAdditionalInfo,
      sizingLabelExist: false
    };
    this.stateService.updateSelectedProduct(selectProduct);
    this.router.navigate(['/store/addproduct']);
  }

  getColumnHeader() {
    let disCol = ['unit'];
    this.productDetails.SizeGuides.forEach((x) => {
      disCol.push(x.SizeName);
    });
    return disCol;
  }
  getDatasource() {
    const sg = this.productDetails.SizeGuides;
    let ds = [];
    var obj = {};
    while (ds.length < 3) {
      for (let i in sg) {
        let v = sg[i];
        switch (ds.length) {
          case 0:
            obj['unit'] = 'Width';
            obj[v.SizeName] = v.Width + ' ' + v.Unit;
            break;
          case 1:
            obj['unit'] = 'Length';
            obj[v.SizeName] = v.Length + ' ' + v.Unit;
            break;
          case 2:
            obj['unit'] = 'Sleeve length';
            obj[v.SizeName] = v.SleeveLength + ' ' + v.Unit;
            break;
        }
      }
      ds.push(obj);
      obj = {};
    }
    return ds;
  }


  fn_gotoPreviousPage() {
    localStorage.setItem("ExistingfilterdData", "true");
    if (localStorage.getItem('userId') == null)
      this.router.navigate(['/masterproducts']);
    else if (this.userRole == "SuperAdmin")
      this.router.navigate(['superadmin/masterproducts']);
    else if (this.userRole == "StoreAdmin" || this.userRole === 'StoreOwner' || this.userRole === 'StoreStaffMember')
      this.router.navigate(['store/masterproducts']);
    else
      this.location.back();
  }

  fn_GetSizeQty(productId, styleId) {
    this.loader.show();
    this.productService.getSizeQty(productId, styleId).subscribe((res: any) => {
      if (res.StatusCode === 200) {
        var result = res.Data.ResponseData[0]
        if(result && result.Sizes.length > 0){
          result.Sizes.forEach(element => {
          this.sizeList.find(x => x.ID == element.UniqueSizeId || x.Name == element.Size).AvailableQty = element.InventoryCount
          });
          
        }
      }
      this.loader.hide();
    }, (err: any) => {
      this.loader.hide();
      this.errorhandlerService.handleError(err);
    });
  }

  getLabelArray(labelString: string): string[] {
    const labelsArray = labelString.split(',').slice(0, 5);
    if(labelsArray && labelsArray.length > 0)
      return labelsArray;
    else return []
}
  // openEditTextWindow() {
  //   const data: IProductDetailEditTextData = {
  //     title: this.selectedProduct.name,
  //     description: this.selectedProduct.longDescription
  //   };
  //   const dialogRef = this.dialog.open(ProductDetailEditTextComponent, {
  //     data: data,
  //     width: '400px',
  //   });

  //   dialogRef.afterClosed().subscribe((result: IProductDetailEditTextData) => {
  //     if (result) {
  //       this.selectedProduct.name = result.title;
  //       this.selectedProduct.longDescription = result.description;
  //       this.productService.updateProductText({
  //         title: result.title,
  //         ProductId: this.productId,
  //         description: result.description,
  //         DesignId: this.designId,
  //         StyleId: Number(this.colorList[0].styleId)
  //       }).subscribe((res: APIResponse<{}>) => {
  //         if (res.StatusCode === 200) {
  //           this.notificationService.success('Product has Updated', 'Success');
  //         }
  //       });
  //     }
  //   });
  // }
}

